import React, { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { SignedInContext } from "../AppEnv";
import { SignedInDoctor } from "../auth/interfaces";
import { getPosturalExamPhoto, getPosturalExamDefinition, getPosturalExamData } from "./utils";
import { PosturalTestDefinition, PosturalExamination, PosturalExaminationSelection } from "./interfaces";
import { Row, Spin, Col } from "antd";
import { EXAM_4PHOTOS_SLUGNAME, EXAM_AAII_SLUGNAME, EXAM_ADAMS_SLUGNAME, EXAM_ELICOIDALE_SLUGNAME, EXAM_FUKUDA_SLUGNAME, EXAM_HEAD_SLUGNAME, EXAM_MAGNO_SLUGNAME, EXAM_PODO_SLUGNAME, EXAM_RATE_SLUGNAME, EXAM_ROMBERG_SLUGNAME, EXAM_VISION_SLUGNAME } from "./exams_definitions_utils";
import { Compare4Photos, Compare4Photos2 } from "./compareType/Comapare4Photos";
import { CompareElicoidale, CompareElicoidale2 } from "./compareType/CompareElicoidale";
import { CompareAAII, CompareAAII2 } from "./compareType/CompareAAII";
import { CompareAdams, CompareAdams2 } from "./compareType/CompareAdams";
import { CompareFukuda, CompareFukuda2 } from "./compareType/CompareFukuda";
import { CompareHead, CompareHead2 } from "./compareType/CompareHead";
import { CompareMagno, CompareMagno2 } from "./compareType/CompareMagno";
import { ComparePodo, ComparePodo2 } from "./compareType/ComparePodo";
import { CompareRate, CompareRate2 } from "./compareType/CompareRate";
import { CompareRomberg, CompareRomberg2 } from "./compareType/CompareRomberg";
import { CompareVision, CompareVision2 } from "./compareType/CompareVision";

const ExamCompare = () => {
  const { id, examsIds } = useParams<{ id: string; examsIds: string }>();
  const signedInDoctor: SignedInDoctor = useContext(SignedInContext).signedInDoctor;

  const [loaded, setLoaded] = useState(false);
  const [exam1Images, setExam1Images] = useState<string[]>([]);
  const [exam2Images, setExam2Images] = useState<string[]>([]);
  const [definition, setDefinition] = useState<string[]>([]);
  const [examDefinitions, setExamDefinitions] = useState<PosturalTestDefinition[] | undefined>(undefined);
  const [exam1Data, setExam1Data] = useState<PosturalExamination | undefined>(undefined);
  const [exam2Data, setExam2Data] = useState<PosturalExamination | undefined>(undefined);
  const [exam1Type, setExam1Type] = useState<string[]>([]);

  // Estrae l'id del paziente dall'url (assicurati che sia corretto)
  const currentUrl = window.location.href;
  const urlParts = currentUrl.split('/');
  const patientIdFromUrl = urlParts.includes('patients')
    ? urlParts[urlParts.indexOf('patients') + 1]
    : urlParts[urlParts.indexOf('compare-exams') + 1];
  
  console.log("Exam 1 Data:", exam1Data);
console.log("Exam 2 Data:", exam2Data);


useEffect(() => {
  const fetchImagesAndData = async () => {
    if (examsIds) {
      const examIdsArray = examsIds.split("-");
      const exam1Id = parseInt(examIdsArray[1]);
      const exam2Id = parseInt(examIdsArray[0]);

      // Recupera le immagini per esame 1 ed esame 2
      const exam1ImagesPromises = [0, 1, 2, 3].map((index) =>
        getPosturalExamPhoto(signedInDoctor, parseInt(id || "0"), exam1Id, index)
      );
      const exam2ImagesPromises = [0, 1, 2, 3].map((index) =>
        getPosturalExamPhoto(signedInDoctor, parseInt(id || "0"), exam2Id, index)
      );

      const exam1ImagesRes = await Promise.all(exam1ImagesPromises);
      const exam2ImagesRes = await Promise.all(exam2ImagesPromises);

      setExam1Images(exam1ImagesRes.filter((img): img is string => !!img));
      setExam2Images(exam2ImagesRes.filter((img): img is string => !!img));

      // Recupera i dati del postural exam per ottenere exam1Type
      try {
        const exam1DataRes = await getPosturalExamData(signedInDoctor, parseInt(patientIdFromUrl), exam1Id);
        if (exam1DataRes) {
          setExam1Type([exam1DataRes.type]);  // Imposta exam1Type
        }
        setExam1Data(exam1DataRes);
      } catch (error) {
        console.error("Errore fetching Exam 1 data:", error);
      }

      try {
        const exam2DataRes = await getPosturalExamData(signedInDoctor, parseInt(patientIdFromUrl), exam2Id);
        setExam2Data(exam2DataRes);
      } catch (error) {
        console.error("Errore fetching Exam 2 data:", error);
      }

      setLoaded(true);
    }
  };

  fetchImagesAndData();
}, [examsIds, id, signedInDoctor, patientIdFromUrl]);

useEffect(() => {
  const fetchDefinitions = async () => {
    if (!exam1Type || exam1Type.length === 0) return;  // Aspetta che exam1Type sia valorizzato

    let definitions;
    switch (exam1Type[0]) {
      case "4foto":
        definitions = await getPosturalExamDefinition(signedInDoctor, EXAM_4PHOTOS_SLUGNAME);
        break;
      case "aaii":
        definitions = await getPosturalExamDefinition(signedInDoctor, EXAM_AAII_SLUGNAME);
        break;
      case "rate":
        definitions = await getPosturalExamDefinition(signedInDoctor, EXAM_RATE_SLUGNAME);
        break;
      case "podo":
        definitions = await getPosturalExamDefinition(signedInDoctor, EXAM_PODO_SLUGNAME);
        break;
      case "vision":
        definitions = await getPosturalExamDefinition(signedInDoctor, EXAM_VISION_SLUGNAME);
        break;
      case "magno":
        definitions = await getPosturalExamDefinition(signedInDoctor, EXAM_MAGNO_SLUGNAME);
        break;
      case "capo":
        definitions = await getPosturalExamDefinition(signedInDoctor, EXAM_HEAD_SLUGNAME);
        break;
      case "fukuda":
        definitions = await getPosturalExamDefinition(signedInDoctor, EXAM_FUKUDA_SLUGNAME);
        break;
      case "romberg":
        definitions = await getPosturalExamDefinition(signedInDoctor, EXAM_ROMBERG_SLUGNAME);
        break;
      case "adams":
        definitions = await getPosturalExamDefinition(signedInDoctor, EXAM_ADAMS_SLUGNAME);
        break;
      case "eli":
        definitions = await getPosturalExamDefinition(signedInDoctor, EXAM_ELICOIDALE_SLUGNAME);
        break;
      default:
        definitions = [];
        break;
    }
    
    console.log("Definitions:", definitions);
    setExamDefinitions(definitions);
  };

  fetchDefinitions();
}, [exam1Type, signedInDoctor]);


  // Funzioni per aggiornare le selezioni per ciascun esame
  const updateExamTestSelection = (
    examNumber: 1 | 2,
    testDefIndex: number,
    selectedValue: number | number[],  // Può essere un numero o un array di numeri
    note: string
  ) => {
    // Se selectedValue è un array, lo convertiamo in un numero intero
    const finalSelectedValue = Array.isArray(selectedValue)
      ? parseBase2Array(selectedValue)  // Converti array in numero
      : selectedValue;
  
    console.log(
      `Aggiorno esame ${examNumber}, Test #${testDefIndex}, Valore: ${finalSelectedValue}, Nota: ${note}`
    );
  
    if (examNumber === 1 && exam1Data) {
      setExam1Data((prev) => {
        if (!prev) return prev;
        const newSelections = prev.testSelections ? [...prev.testSelections] : [];
        newSelections[testDefIndex] = {
          ...newSelections[testDefIndex],
          selectedValue: finalSelectedValue,  // Usa il valore convertito
          note,
        };
        return { ...prev, testSelections: newSelections };
      });
    } else if (examNumber === 2 && exam2Data) {
      setExam2Data((prev) => {
        if (!prev) return prev;
        const newSelections = prev.testSelections ? [...prev.testSelections] : [];
        newSelections[testDefIndex] = {
          ...newSelections[testDefIndex],
          selectedValue: finalSelectedValue,  // Usa il valore convertito
          note,
        };
        return { ...prev, testSelections: newSelections };
      });
    }
  };
  
  

  if (!loaded) {
    return (
      <div style={{ textAlign: "center", marginTop: "20px" }}>
        <Spin size="large" />
        <p>Caricamento in corso...</p>
      </div>
    );
  }

  return (
    <div style={{ marginLeft: "32px", marginTop: "32px"  }}>
      <Row >
        <Col span={11} style={{ textAlign: "right" }}>
          <h3>Esame 1</h3>
          {renderCompareComponent(
            exam1Type[0],       // ad es. “4foto”
            exam1Images,
            examDefinitions,
            exam1Data || { examId:0, patientId:0, type:"", doctorId:0, medicalStudioId:0, testSelections:[] },
            (testDefId, selectedValue, note) => updateExamTestSelection(1, testDefId, selectedValue, note),
            1
          )}
        </Col>
       <div style={{ width: "15px" }}></div>
        <Col span={11}>
          <h3>Esame 2</h3>
          {renderCompareComponent(
            exam2Data?.type || "",
            exam2Images,
            examDefinitions,
            exam2Data || { examId:0, patientId:0, type:"", doctorId:0, medicalStudioId:0, testSelections:[] },
            (testDefId, selectedValue, note) => updateExamTestSelection(2, testDefId, selectedValue, note),
            2
          )}
        </Col>
      </Row>
    </div>
  );
};

// Funzione che sceglie quale componente di “compare” utilizzare
function renderCompareComponent(
  examType: string,
  images: string[],
  examDefinitions: PosturalTestDefinition[] | undefined,
  examData: PosturalExamination,
  setTestSelection: (testDefId: number, selectedValue: number|number[], note: string) => void,
  examNumber: 1 | 2
) {
  switch (examType) {
    // Se è “4foto”, per exam1 → Compare4Photos, per exam2 → Compare4Photos2
    case "anam":
    case "stim":
    case "bl":
    case "tinetti":
    case "stim":
    case "stim":
      return  (

<>
<div> Esame non Comparabile. </div>
</>
      );

    

  case "4foto":
      return examDefinitions && examNumber === 1 ? (
         
        <Compare4Photos
        patient={examData.patientId} 
          page={1}
          images={images.map((url) => ({ url, uploaded: true }))}
          getWebcamPhoto={() => {}}
          loadPhoto={() => {}}
          testDefinitions={examDefinitions || []}
          posturalExamination={examData}
          setTestSelection={setTestSelection}
        />
      ) : (
        <Compare4Photos2
        patient={examData.patientId} 
          page={1}
          images={images.map((url) => ({ url, uploaded: true }))}
          getWebcamPhoto={() => {}}
          loadPhoto={() => {}}
          testDefinitions={examDefinitions || []}
          posturalExamination={examData}
          setTestSelection={setTestSelection}
        />
      );


  case "aaii":
      return examNumber === 1 ? (
        <CompareAAII 
        patient={examData.patientId} 
        page={1}
        images={images.map((url) => ({ url, uploaded: true }))}
        getWebcamPhoto={() => {}}
        loadPhoto={() => {}}
        testDefinitions={examDefinitions || []}
        posturalExamination={examData}
        setTestSelection={setTestSelection}
        />
      ) : (
        <CompareAAII2
        patient={examData.patientId} 
          page={1}
          images={images.map((url) => ({ url, uploaded: true }))}
          getWebcamPhoto={() => {}}
          loadPhoto={() => {}}
          testDefinitions={examDefinitions || []}
          posturalExamination={examData}
          setTestSelection={setTestSelection}
        />
      );

  
  case "rate":
      return examNumber === 1 ? (
        <CompareRate 
        page={1}
        images={images.map((url) => ({ url, uploaded: true }))}
        getWebcamPhoto={() => {}}
        loadPhoto={() => {}}
        testDefinitions={examDefinitions || []}
        posturalExamination={examData}
        setTestSelection={setTestSelection}
        />
      ) : (
        <CompareRate2
          page={1}
          images={images.map((url) => ({ url, uploaded: true }))}
          getWebcamPhoto={() => {}}
          loadPhoto={() => {}}
          testDefinitions={examDefinitions || []}
          posturalExamination={examData}
          setTestSelection={setTestSelection}
        />
      );
  
  case "podo":
        return examNumber === 1 ? (
          <ComparePodo 
          page={1}
          images={images.map((url) => ({ url, uploaded: true }))}
          getWebcamPhoto={() => {}}
          loadPhoto={() => {}}
          testDefinitions={examDefinitions || []}
          posturalExamination={examData}
          setTestSelection={setTestSelection}
          />
        ) : (
          <ComparePodo2
            page={1}
            images={images.map((url) => ({ url, uploaded: true }))}
            getWebcamPhoto={() => {}}
            loadPhoto={() => {}}
            testDefinitions={examDefinitions || []}
            posturalExamination={examData}
            setTestSelection={setTestSelection}
          />
        );
  
  case "vision":
          return examNumber === 1 ? (
            <CompareVision 
            page={1}
            images={images.map((url) => ({ url, uploaded: true }))}
            getWebcamPhoto={() => {}}
            loadPhoto={() => {}}
            testDefinitions={examDefinitions || []}
            posturalExamination={examData}
            setTestSelection={setTestSelection}
            />
          ) : (
            <CompareVision2
              page={1}
              images={images.map((url) => ({ url, uploaded: true }))}
              getWebcamPhoto={() => {}}
              loadPhoto={() => {}}
              testDefinitions={examDefinitions || []}
              posturalExamination={examData}
              setTestSelection={setTestSelection}
            />
          );
      
  
  case "magno":
            return examNumber === 1 ? (
              <CompareMagno 
              page={1}
              images={images.map((url) => ({ url, uploaded: true }))}
              getWebcamPhoto={() => {}}
              loadPhoto={() => {}}
              testDefinitions={examDefinitions || []}
              posturalExamination={examData}
              setTestSelection={setTestSelection}
              />
            ) : (
              <CompareMagno2
                page={1}
                images={images.map((url) => ({ url, uploaded: true }))}
                getWebcamPhoto={() => {}}
                loadPhoto={() => {}}
                testDefinitions={examDefinitions || []}
                posturalExamination={examData}
                setTestSelection={setTestSelection}
              />
            );
  
  case "capo":
                return examNumber === 1 ? (
                <CompareHead 
                patient={examData.patientId} 
                page={1}
                images={images.map((url) => ({ url, uploaded: true }))}
                getWebcamPhoto={() => {}}
                loadPhoto={() => {}}
                testDefinitions={examDefinitions || []}
                posturalExamination={examData}
                setTestSelection={setTestSelection}
                />
              ) : (
                <CompareHead2
                patient={examData.patientId} 
                  page={1}
                  images={images.map((url) => ({ url, uploaded: true }))}
                  getWebcamPhoto={() => {}}
                  loadPhoto={() => {}}
                  testDefinitions={examDefinitions || []}
                  posturalExamination={examData}
                  setTestSelection={setTestSelection}
                />
              );
    
      
  case "fukuda":
                return examNumber === 1 ? (
                  <CompareFukuda
                  page={1}
                  images={images.map((url) => ({ url, uploaded: true }))}
                  getWebcamPhoto={() => {}}
                  loadPhoto={() => {}}
                  testDefinitions={examDefinitions || []}
                  posturalExamination={examData}
                  setTestSelection={setTestSelection}
                  />
                ) : (
                  <CompareFukuda2
                    page={1}
                    images={images.map((url) => ({ url, uploaded: true }))}
                    getWebcamPhoto={() => {}}
                    loadPhoto={() => {}}
                    testDefinitions={examDefinitions || []}
                    posturalExamination={examData}
                    setTestSelection={setTestSelection}
                  />
                );
  
  case "romberg":
                  return examNumber === 1 ? (
                    <CompareRomberg 
                    page={1}
                    images={images.map((url) => ({ url, uploaded: true }))}
                    getWebcamPhoto={() => {}}
                    loadPhoto={() => {}}
                    testDefinitions={examDefinitions || []}
                    posturalExamination={examData}
                    setTestSelection={setTestSelection}
                    />
                  ) : (
                    <CompareRomberg2
                      page={1}
                      images={images.map((url) => ({ url, uploaded: true }))}
                      getWebcamPhoto={() => {}}
                      loadPhoto={() => {}}
                      testDefinitions={examDefinitions || []}
                      posturalExamination={examData}
                      setTestSelection={setTestSelection}
                    />
                  );
                      
  
  case "adams":
                    return examNumber === 1 ? (
                      <CompareAdams 
                      page={1}
                      images={images.map((url) => ({ url, uploaded: true }))}
                      getWebcamPhoto={() => {}}
                      loadPhoto={() => {}}
                      testDefinitions={examDefinitions || []}
                      posturalExamination={examData}
                      setTestSelection={setTestSelection}
                      />
                    ) : (
                      <CompareAdams2
                        page={1}
                        images={images.map((url) => ({ url, uploaded: true }))}
                        getWebcamPhoto={() => {}}
                        loadPhoto={() => {}}
                        testDefinitions={examDefinitions || []}
                        posturalExamination={examData}
                        setTestSelection={setTestSelection}
                      />
                    );

                    
              
    case "eli":
      return examNumber === 1 ? (
        <CompareElicoidale
        page={1}
        images={images.map((url) => ({ url, uploaded: true }))}
        getWebcamPhoto={() => {}}
        loadPhoto={() => {}}
        testDefinitions={examDefinitions || []}
        posturalExamination={examData}
        setTestSelection={setTestSelection}
        />
      ) : (
        <CompareElicoidale2
          page={1}
          images={images.map((url) => ({ url, uploaded: true }))}
          getWebcamPhoto={() => {}}
          loadPhoto={() => {}}
          testDefinitions={examDefinitions || []}
          posturalExamination={examData}
          setTestSelection={setTestSelection}
        />
      );
    default:
      return null;
  }
}

export default ExamCompare;

function parseBase2Array(selectedValue: number[]): number {
  return selectedValue.reduce((acc, curr) => acc + curr, 0);
}

